import { useEffect, useRef, useState } from 'react';
import { MAX_RADIUS_MILES, type PostalPoint, type ZipBoundary } from '../shared/contracts';

export function useZipBoundaries(zipKey: string, enabled: boolean) {
  const cache = useRef<Record<string, ZipBoundary | null>>({});
  const [boundaries, setBoundaries] = useState(cache.current);
  const [errors, setErrors] = useState<string[]>([]);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!enabled || !zipKey) return;
    const controller = new AbortController(); let index = 0;
    const needed = zipKey.split(',').filter(zip => !(zip in cache.current));
    setErrors([]);
    const worker = async () => {
      while (index < needed.length && !controller.signal.aborted) {
        const zip = needed[index++]!;
        try {
          const response = await fetch(`/api/postal/${zip}/boundary`, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Boundary unavailable');
          const result = await response.json() as { boundary: ZipBoundary | null };
          if (controller.signal.aborted) return;
          cache.current = { ...cache.current, [zip]: result.boundary };
          setBoundaries(cache.current);
        } catch { if (!controller.signal.aborted) setErrors(previous => [...previous, zip]); }
      }
    };
    void Promise.all(Array.from({ length: Math.min(3, needed.length) }, worker));
    return () => controller.abort();
  }, [zipKey, enabled, retry]);
  const zips = zipKey ? zipKey.split(',') : [];
  return { boundaries, errors, missing: zips.filter(zip => boundaries[zip] === null),
    loading: enabled && zips.some(zip => !(zip in boundaries) && !errors.includes(zip)), retry: () => setRetry(value => value + 1) };
}

export function useNearbyPostal(zipKey: string) {
  const cache = useRef<Record<string, PostalPoint[]>>({});
  const [points, setPoints] = useState<PostalPoint[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const controller = new AbortController(); let index = 0;
    const needed = (zipKey ? zipKey.split(',') : []).filter(zip => !(zip in cache.current));
    setErrors([]); setLoading(needed.length > 0);
    const worker = async () => {
      while (index < needed.length && !controller.signal.aborted) {
        const zip = needed[index++]!;
        try {
          const response = await fetch(`/api/postal/${zip}/nearby?range=${MAX_RADIUS_MILES}`, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('ZIP lookup unavailable');
          const result = await response.json() as PostalPoint[];
          if (controller.signal.aborted) return;
          cache.current = { ...cache.current, [zip]: result };
          setPoints([...new Map(Object.values(cache.current).flat().map(point => [point.zip, point])).values()]);
        } catch { if (!controller.signal.aborted) setErrors(previous => [...previous, zip]); }
      }
    };
    void Promise.all(Array.from({ length: Math.min(3, needed.length) }, worker)).then(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [zipKey,retry]);
  return { points, errors, loading, loadedZips: Object.keys(cache.current), retry: () => setRetry(value => value+1) };
}
